import React, { useState, useEffect, useRef } from 'react';
import { Edit2, Save, X, MapPin } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';

const MarketValueBail = ({ onBack, setSelectedComponent }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // ─── Master Market Value State ────────────────────────────────────────────
  const [masterData, setMasterData] = useState([]);
  const [editMasterRowIndex, setEditMasterRowIndex] = useState(null);
  const [editMasterFormData, setEditMasterFormData] = useState({ amount: '' });
  const [masterLoading, setMasterLoading] = useState(false);

  const [editRowIndex, setEditRowIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({ irrigable_area: '', group_valuation: '' });
  const [loading, setLoading] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [proposalData, setProposalData] = useState(null);
  const [usersLAO, setusersLAO] = useState(null);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const { language, translations } = useLanguage();

  // ─── Location from selectedProposal ──────────────────────────────────────
  const [proposalDistrict, setProposalDistrict] = useState('');
  const [proposalTaluka, setProposalTaluka] = useState('');
  const [proposalVillage, setProposalVillage] = useState('');

  const ROLES = { COLLECTOR: 1, LAO: 3, SAMANVAY: 6, PROJECT_INCHARGE: 18, DATA_ENTRY: 14 };
  const userData = JSON.parse(localStorage.getItem('userData'));
  const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);
  const tableRef = useRef(null);

  // ─── Extract location from selectedProposal ───────────────────────────────
  useEffect(() => {
    try {
      const selProposalStr = localStorage.getItem('selectedProposal');
      if (selProposalStr) {
        const proposal = JSON.parse(selProposalStr);
        setProposalDistrict(proposal.dist_name || proposal.distName || '');
        setProposalTaluka(proposal.taluka_name || proposal.talukaName || '');
        setProposalVillage(proposal.village_name || proposal.villageName || '');
      }
    } catch (err) {
      console.error('Error reading selectedProposal:', err);
    }
  }, []);

  // ─── Fetch projectId ──────────────────────────────────────────────────────
  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          return { id: projectData.project_id || projectData.id, name: projectData.project_name || projectData.name };
        } catch (e) { console.error(e); }
      }
      const selectedProject = localStorage.getItem('selectedProject');
      if (selectedProject) {
        try {
          const project = JSON.parse(selectedProject);
          return { id: project.project_id || project.id, name: project.project_name || project.name };
        } catch (e) { console.error(e); }
      }
      const ud = localStorage.getItem('userData');
      if (ud) {
        try {
          const user = JSON.parse(ud);
          return { id: user.data?.project_id || user.project_id, name: user.data?.project_name || user.project_name || 'Unknown Project' };
        } catch (e) { console.error(e); }
      }
      return { id: null, name: null };
    };
    const projectInfo = getProjectInfo();
    setProjectId(projectInfo.id);
    setProjectName(projectInfo.name);
  }, []);

  // ─── Fetch proposalId ─────────────────────────────────────────────────────
  useEffect(() => {
    const getProposalInfo = () => {
      const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');
      return {
        proposalId: currentProposal?.data?.default_proposal ||
          currentProposal?.data?.proposal_id ||
          currentProposal?.default_proposal ||
          currentProposal?.proposal_id || null
      };
    };
    setProposalId(getProposalInfo().proposalId);
  }, []);

  useEffect(() => {
    fetchProposalData();
    fetchusersLAO();
  }, [projectId, proposalId]);

  const fetchProposalData = async () => {
    try {
      const response = await apiService.getProposalPublish(projectId, proposalId);
      if (response.status === 200 && response.data) setProposalData(response.data);
    } catch (err) { console.error('Error fetching proposal data:', err); }
  };

  const fetchusersLAO = async () => {
    try {
      const response = await apiService.getAssignLAO(3, projectId, proposalId);
      setusersLAO(response.status === 200 && response.data ? (Array.isArray(response.data) ? response.data : []) : []);
    } catch (error) { console.error('Error fetching LAO users:', error); setusersLAO([]); }
  };

  // ─── Build location query params ──────────────────────────────────────────
  const buildLocationParams = () => {
    let params = '';
    if (proposalDistrict) params += `&distName=${encodeURIComponent(proposalDistrict)}`;
    if (proposalTaluka) params += `&talukaName=${encodeURIComponent(proposalTaluka)}`;
    if (proposalVillage) params += `&villageName=${encodeURIComponent(proposalVillage)}`;
    return params;
  };

  // ─── ONE API CALL — sets both masterData and filteredData ─────────────────
  const loadAllData = async () => {
    if (!projectId || !proposalId) return;

    setLoadingRecords(true);
    try {
      const locationParams = buildLocationParams();
      const response = await apiService.getOwnerMarketValue(projectId, proposalId, locationParams);
      console.log("API Full Response:", response);

      const responseArray = Array.isArray(response?.data) ? response.data : [];
      setData(responseArray);
      setFilteredData(responseArray);

      // Extract masterValueData from first village (or all if needed)
      const allMasterValues = responseArray.flatMap(village => village.masterValueData || []);
      const uniqueMaster = allMasterValues.filter(
        (item, idx, self) => idx === self.findIndex(m => m.group_no === item.group_no)
      );
      uniqueMaster.sort((a, b) => a.group_no - b.group_no);
      setMasterData(uniqueMaster);

    } catch (error) {
      console.error('Error fetching market value data:', error);
      setMasterData([]);
      setData([]);
      setFilteredData([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  // ─── Load when projectId, proposalId, or location ready ──────────────────
  useEffect(() => {
    if (projectId && proposalId) {
      loadAllData();
    }
  }, [projectId, proposalId, proposalDistrict, proposalTaluka, proposalVillage]);

  // ─── Master table edit handlers ───────────────────────────────────────────
  const handleMasterEditClick = (dtvId, index, row) => {
    const masterKey = `${dtvId}-${index}`;
    setEditMasterRowIndex(masterKey);
    setEditMasterFormData({ amount: row.amount || '' });
  };

  const handleMasterSave = async (dtvId, index) => {
    const masterKey = `${dtvId}-${index}`;
    const villageGroup = filteredData.find(v => v.dtv_id === dtvId);
    const row = villageGroup?.masterValueData?.[index];

    if (!row?.value_id) { alert('value_id not found.'); return; }

    try {
      setMasterLoading(true);
      const result = await apiService.postAddUpdateMasterMarketValue(row.value_id, { amount: editMasterFormData.amount });

      if (result.status === 200) {
        alert('✅ Master Market Value Updated Successfully');
        // update in-place inside filteredData
        setFilteredData(prev => prev.map(v => {
          if (v.dtv_id !== dtvId) return v;
          const updatedMaster = [...v.masterValueData];
          updatedMaster[index] = { ...updatedMaster[index], amount: editMasterFormData.amount };
          return { ...v, masterValueData: updatedMaster };
        }));
        setEditMasterRowIndex(null);
      } else {
        alert('Failed to update master market value.');
      }
    } catch (error) {
      console.error('Error updating master market value:', error);
      alert('❌ Error while updating master market value.');
    } finally {
      setMasterLoading(false);
    }
  };

  const handleMasterInputChange = (e) => {
    setEditMasterFormData({ amount: e.target.value });
  };

  const handleMasterCancel = () => {
    setEditMasterRowIndex(null);
    setEditMasterFormData({ amount: '' });
  };

  // ─── Owner table edit handlers ────────────────────────────────────────────
  const handleEditClick = (villageGroup, index, globalIndex) => {
    const row = villageGroup.data[index];
    setEditRowIndex(globalIndex);
    setEditFormData({
      irrigable_area: row.irrigable_area || '',
      group_valuation: row.group_valuation || '',
      market_amount: row.market_amount || '',
    });
  };

  const handleSave = async (villageGroup, index, globalIndex) => {
    if (!projectId || !proposalId) { alert('No project or proposal ID found.'); return; }
    try {
      setLoading(true);
      const row = villageGroup.data[index];
      const updateData = {
        survey_no: row.survey_no,
        irrigable_area: editFormData.irrigable_area,
        group_valuation: editFormData.group_valuation,
        market_amount: editFormData.market_amount,
        project_id: projectId,
        proposal_id: proposalId,
        dist_name: villageGroup.dist_name,
        taluka_name: villageGroup.taluka_name,
        village_name: villageGroup.village_name,
      };

      const result = await apiService.postAddUpdateOwnerMarketValue(projectId, proposalId, updateData);
      if (result.status === 200) {
        alert('✅ Market Value Updated Successfully');
        setEditRowIndex(null);
        await loadAllData();
      } else {
        alert('Failed to update market value.');
      }
    } catch (error) {
      console.error('Error updating market value:', error);
      alert('❌ Error while updating market value.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setEditRowIndex(null);
    setEditFormData({ irrigable_area: '', group_valuation: '' });
  };

  // ─── Print ────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!tableRef.current) { alert("No content available to print."); return; }
    const printableTable = `
      <table style="margin-top:20px; width:100%; border-collapse:collapse; border:1px solid #666;">
        <thead>
          <tr>
            <th style="border:1px solid #666; padding:8px;">${translations[language]?.MarketGatNumber || 'गट क्रमांक'}</th>
            <th style="border:1px solid #666; padding:8px;">${translations[language]?.EligibleArea || 'पात्र क्षेत्र'}</th>
            <th style="border:1px solid #666; padding:8px;">${translations[language]?.DeductionReason || 'कपात कारण'}</th>
            <th style="border:1px solid #666; padding:8px;">${translations[language]?.HectareDeduction || 'हेक्टर कपात'}</th>
            <th style="border:1px solid #666; padding:8px;">${translations[language]?.AreaGat || 'क्षेत्र गट'}</th>
            <th style="border:1px solid #666; padding:8px;">${translations[language]?.HectareRate || 'हेक्टर दर'}</th>
          </tr>
        </thead>
        <tbody>
          ${filteredData.map((row, i) => `
            <tr style="background-color:${i % 2 === 0 ? '#fff' : '#f3f4f6'};">
              <td style="border:1px solid #666; padding:8px;">${row.survey_no}</td>
              <td style="border:1px solid #666; padding:8px;">${row.irrigable_area}</td>
              <td style="border:1px solid #666; padding:8px;">${row.group_valuation}</td>
              <td style="border:1px solid #666; padding:8px;">${row.hectareAakarni}</td>
              <td style="border:1px solid #666; padding:8px;">${row.aakarGat}</td>
              <td style="border:1px solid #666; padding:8px;">${row.market_amount}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>`;

    const win = window.open('', '_blank');
    win.document.write(`
      <html><head>
        <style>
          @page { size:A4 portrait; margin-top:0.19in; margin-bottom:0.25in; margin-left:0.81in; margin-right:0.5in; }
          body { font-family:'DVOT-Surekh','Mangal','Arial',sans-serif; }
          table { width:100%; border-collapse:collapse; }
          th,td { border:1px solid #666; padding:8px; text-align:center; }
          .title { text-align:center; font-size:18px; margin-bottom:6px; }
          .signature-block { text-align:right; padding-top:2rem; }
        </style>
      </head><body>
        <div class="title">प्रकल्प नाव: ${projectName} | प्रस्ताव नाव/क्रमांक: ${proposalData?.section_11_bhusampadan_no || ''}</div>
        <div class="title">${translations[language]?.MarketPriceChart || 'बाजार मूल्य तक्ता'}</div>
        ${printableTable}
        <div class="signature-block">
          <p>${usersLAO?.[0]?.full_name || ''}</p>
          <p>${usersLAO?.[0]?.designation || ''}</p>
        </div>
      </body></html>`);
    win.document.close();
    win.print();
  };

  // ─── Loader ───────────────────────────────────────────────────────────────
  if (loadingRecords) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
        <div className="flex justify-center items-center h-80">
          <div className="text-center">
            <div className="inline-flex relative">
              <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
              <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0"
                style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
            </div>
            <p className="text-gray-400 mt-4">{translations[language]?.LoadingMarketPrices}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── Action Bar ── */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex justify-between items-center gap-4">
            {/* <div className="text-sm text-gray-500">
              {proposalDistrict && (
                <span>📍 {proposalDistrict}{proposalTaluka ? ` › ${proposalTaluka}` : ''}{proposalVillage ? ` › ${proposalVillage}` : ''}</span>
              )}
            </div> */}
            <button
              onClick={handlePrint}
              disabled={filteredData.length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {translations[language]?.Print || 'Print'}
            </button>
          </div>
        </div>

        {filteredData.length > 0 && filteredData.map((villageGroup) => (
          <div key={villageGroup.dtv_id} className="space-y-4">

            {/* ── Village Header ── */}
            <div className="bg-cyan-700 rounded-2xl shadow-xl p-4 text-center text-white">
              <h2 className="text-lg font-bold">
                जि. {villageGroup.dist_name} ता. {villageGroup.taluka_name} मोजे {villageGroup.village_name}
              </h2>
            </div>

            {/* ── TABLE 1: Master Market Value ── */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="text-center p-4 border-b">
                <h2 className="text-xl font-bold text-gray-800">
                  {translations[language]?.MasterMarketValueTable || 'Market Value Table'}
                </h2>
              </div>
              {(!villageGroup.masterValueData || villageGroup.masterValueData.length === 0) ? (
                <div className="text-center py-10 text-gray-400">
                  <p>{translations[language]?.NoMasterMarketData || 'No market value data found.'}</p>
                </div>
              ) : (
                <div className="overflow-x-auto p-4">
                  <table className="w-full border border-gray-400 text-center text-sm">
                    <thead>
                      <tr className="bg-cyan-700 text-white">
                        {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                          <th className="border border-gray-400 px-2 py-2">Action</th>
                        )}
                        <th className="border border-gray-400 px-2 py-2">Group No</th>
                        <th className="border border-gray-400 px-2 py-2">From Range</th>
                        <th className="border border-gray-400 px-2 py-2">To Range</th>
                        <th className="border border-gray-400 px-2 py-2">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {villageGroup.masterValueData.map((row, index) => {
                        const masterKey = `${villageGroup.dtv_id}-${index}`;
                        return (
                          <tr key={row.value_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                              <td className="border border-gray-400 px-2 py-1">
                                {editMasterRowIndex === masterKey ? (
                                  <div className="flex justify-center gap-2">
                                    <button onClick={() => handleMasterSave(villageGroup.dtv_id, index)} disabled={masterLoading}
                                      className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400">
                                      {masterLoading
                                        ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        : <Save size={16} />}
                                    </button>
                                    <button onClick={handleMasterCancel}
                                      className="flex items-center gap-1 px-2 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                                      <X size={16} />
                                    </button>
                                  </div>
                                ) : (
                                  <button onClick={() => handleMasterEditClick(villageGroup.dtv_id, index, row)}
                                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                    <Edit2 size={16} />
                                  </button>
                                )}
                              </td>
                            )}
                            <td className="border border-gray-400 px-2 py-1">{row.group_no}</td>
                            <td className="border border-gray-400 px-2 py-1">{row.from_range}</td>
                            <td className="border border-gray-400 px-2 py-1">{row.to_range ?? '—'}</td>
                            <td className="border border-gray-400 px-2 py-1">
                              {editMasterRowIndex === masterKey ? (
                                <input type="number" value={editMasterFormData.amount}
                                  onChange={handleMasterInputChange}
                                  className="w-full px-2 py-1 border border-gray-300 rounded-md text-center" />
                              ) : (row.amount ?? '—')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ── TABLE 2: Owner Market Value ── */}
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              <div className="text-center p-4 border-b">
                <h2 className="text-xl font-bold text-gray-800">
                  {translations[language]?.MarketPriceChart || 'बाजार मूल्य तक्ता'}
                </h2>
              </div>
              <div ref={tableRef} className="overflow-x-auto  p-4">
                <table className="w-full border border-gray-400 text-center text-sm">
                  <thead>
                    <tr className="bg-cyan-700 text-white">
                      {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                        <th className="border border-gray-400 px-2 py-2">{translations[language]?.MarketAction}</th>
                      )}
                      <th className="border border-gray-400 px-2 py-2">{translations[language]?.MarketGatNumber}</th>
                      <th className="border border-gray-400 px-2 py-2">{translations[language]?.EligibleArea}</th>
                      <th className="border border-gray-400 px-2 py-2">{translations[language]?.DeductionReason}</th>
                      <th className="border border-gray-400 px-2 py-2">{translations[language]?.HectareDeduction}</th>
                      <th className="border border-gray-400 px-2 py-2">{translations[language]?.AreaGat}</th>
                      <th className="border border-gray-400 px-2 py-2">{translations[language]?.HectareRate}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(villageGroup.data || []).map((row, index) => {
                      const globalIndex = `${villageGroup.dtv_id}-${index}`;
                      return (
                        <tr key={globalIndex} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>
                          {(userRole === ROLES.LAO || userRole === ROLES.DATA_ENTRY) && (
                            <td className="border border-gray-400 px-2 py-1">
                              {editRowIndex === globalIndex ? (
                                <div className="flex justify-center gap-2">
                                  <button onClick={() => handleSave(villageGroup, index, globalIndex)} disabled={loading}
                                    className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400">
                                    {loading
                                      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      : <Save size={16} />}
                                  </button>
                                  <button onClick={handleCancel}
                                    className="flex items-center gap-1 px-2 py-1 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">
                                    <X size={16} />
                                  </button>
                                </div>
                              ) : (
                                <button onClick={() => handleEditClick(villageGroup, index, globalIndex)}
                                  className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                                  <Edit2 size={16} />
                                </button>
                              )}
                            </td>
                          )}
                          <td className="border border-gray-400 px-2 py-1">{row.survey_no}</td>
                          <td className="border border-gray-400 px-2 py-1">
                            {editRowIndex === globalIndex
                              ? <input type="text" name="irrigable_area" value={editFormData.irrigable_area} onChange={handleInputChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" />
                              : (row.irrigable_area || '—')}
                          </td>
                          <td className="border border-gray-400 px-2 py-1">
                            {editRowIndex === globalIndex
                              ? <input type="text" name="group_valuation" value={editFormData.group_valuation} onChange={handleInputChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" />
                              : (row.group_valuation || '—')}
                          </td>
                          <td className="border border-gray-400 px-2 py-1">{row.rate_per_hectare || '—'}</td>
                          <td className="border border-gray-400 px-2 py-1">{row.market_group_no || '—'}</td>
                          <td className="border border-gray-400 px-2 py-1">
                            {editRowIndex === globalIndex
                              ? <input type="text" name="market_amount" value={editFormData.market_amount} onChange={handleInputChange} className="w-full px-2 py-1 border border-gray-300 rounded-md" />
                              : (row.market_amount || '—')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        ))}

        {/* ── No Data ── */}
        {filteredData.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <MapPin className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{translations[language]?.NoRecordsFound || 'No Records Found'}</h3>
            <p className="text-gray-500 text-sm">{translations[language]?.NoMarketValueData || 'No market value data found.'}</p>
          </div>
        )}

        {/* fallback if no data at all */}
        {filteredData.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="text-center p-4 border-b">
              <h2 className="text-xl font-bold text-gray-800">
                {translations[language]?.MasterMarketValueTable || 'Market Value Table'}
              </h2>
            </div>
            <div className="text-center py-10 text-gray-400">
              <p>{translations[language]?.NoMasterMarketData || 'No market value data found.'}</p>
            </div>
          </div>
        )}

        {/* No Data */}
        {filteredData.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <MapPin className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">{translations[language]?.NoRecordsFound || 'No Records Found'}</h3>
            <p className="text-gray-500 text-sm">{translations[language]?.NoMarketValueData || 'No market value data found.'}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketValueBail;